import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

const DEVICE = (() => {
  if (typeof navigator === "undefined") {
    return null;
  }

  const ua = navigator.userAgent;

  return {
    android() {
      return /Android/i.test(ua);
    },
    blackBerry() {
      return /BlackBerry/i.test(ua);
    },
    ieMobile() {
      return /IEMobile/i.test(ua);
    },
    ios() {
      return /iPhone|iPad|iPod/i.test(ua);
    },
    iPad() {
      return /Mac/.test(ua) && navigator.maxTouchPoints && navigator.maxTouchPoints > 2;
    },
    operaMini() {
      return /Opera Mini/i.test(ua);
    },
    any() {
      return (
        this.android() ||
        this.blackBerry() ||
        this.ios() ||
        this.iPad() ||
        this.operaMini() ||
        this.ieMobile()
      );
    },
  };
})();

const DEFAULT_CLICKABLES = [
  "a",
  'input[type="text"]',
  'input[type="email"]',
  'input[type="number"]',
  'input[type="submit"]',
  'input[type="image"]',
  "label[for]",
  "select",
  "textarea",
  "button",
  ".link",
];

function useEventListener(eventName, handler, element = document) {
  const savedHandler = useRef(handler);

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!element || !element.addEventListener) {
      return undefined;
    }

    const eventListener = (event) => savedHandler.current(event);
    element.addEventListener(eventName, eventListener);

    return () => {
      element.removeEventListener(eventName, eventListener);
    };
  }, [eventName, element]);
}

function CursorCore({
  outerStyle,
  innerStyle,
  color = "220, 90, 90",
  outerAlpha = 0.3,
  innerSize = 8,
  outerSize = 8,
  outerScale = 6,
  innerScale = 0.6,
  trailingSpeed = 5,
  clickables = DEFAULT_CLICKABLES,
}) {
  const cursorOuterRef = useRef(null);
  const cursorInnerRef = useRef(null);
  const requestRef = useRef(null);
  const previousTimeRef = useRef();
  const coordsRef = useRef({ x: 0, y: 0 });
  const endRef = useRef({ x: 0, y: 0 });

  const [isVisible, setIsVisible] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isActiveClickable, setIsActiveClickable] = useState(false);

  const onMouseMove = useCallback(({ clientX, clientY }) => {
    if (!cursorInnerRef.current) {
      return;
    }

    cursorInnerRef.current.style.top = `${clientY}px`;
    cursorInnerRef.current.style.left = `${clientX}px`;
    endRef.current = { x: clientX, y: clientY };
  }, []);

  const animateOuterCursor = useCallback(
    (time) => {
      if (previousTimeRef.current !== undefined && cursorOuterRef.current) {
        const easing = Math.max(trailingSpeed, 1);
        const nextCoords = coordsRef.current;
        nextCoords.x += (endRef.current.x - nextCoords.x) / easing;
        nextCoords.y += (endRef.current.y - nextCoords.y) / easing;
        cursorOuterRef.current.style.top = `${nextCoords.y}px`;
        cursorOuterRef.current.style.left = `${nextCoords.x}px`;
      }

      previousTimeRef.current = time;
      requestRef.current = requestAnimationFrame(animateOuterCursor);
    },
    [trailingSpeed]
  );

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animateOuterCursor);

    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [animateOuterCursor]);

  const onMouseDown = useCallback(() => setIsActive(true), []);
  const onMouseUp = useCallback(() => setIsActive(false), []);
  const onMouseEnterViewport = useCallback(() => setIsVisible(true), []);
  const onMouseLeaveViewport = useCallback(() => setIsVisible(false), []);

  useEventListener("mousemove", onMouseMove);
  useEventListener("mousedown", onMouseDown);
  useEventListener("mouseup", onMouseUp);
  useEventListener("mouseover", onMouseEnterViewport);
  useEventListener("mouseout", onMouseLeaveViewport);

  useEffect(() => {
    if (!cursorInnerRef.current || !cursorOuterRef.current) {
      return;
    }

    if (isActive) {
      cursorInnerRef.current.style.transform = `translate(-50%, -50%) scale(${innerScale})`;
      cursorOuterRef.current.style.transform = `translate(-50%, -50%) scale(${outerScale})`;
      return;
    }

    cursorInnerRef.current.style.transform = "translate(-50%, -50%) scale(1)";
    cursorOuterRef.current.style.transform = "translate(-50%, -50%) scale(1)";
  }, [innerScale, outerScale, isActive]);

  useEffect(() => {
    if (!isActiveClickable || !cursorInnerRef.current || !cursorOuterRef.current) {
      return;
    }

    cursorInnerRef.current.style.transform = `translate(-50%, -50%) scale(${innerScale * 1.2})`;
    cursorOuterRef.current.style.transform = `translate(-50%, -50%) scale(${outerScale * 1.4})`;
  }, [innerScale, outerScale, isActiveClickable]);

  useEffect(() => {
    if (!cursorInnerRef.current || !cursorOuterRef.current) {
      return;
    }

    cursorInnerRef.current.style.opacity = isVisible ? "1" : "0";
    cursorOuterRef.current.style.opacity = isVisible ? "1" : "0";
  }, [isVisible]);

  const onClickableMouseOver = useCallback(() => {
    setIsActive(true);
  }, []);
  const onClickableClick = useCallback(() => {
    setIsActive(true);
    setIsActiveClickable(false);
  }, []);
  const onClickableMouseDown = useCallback(() => {
    setIsActiveClickable(true);
  }, []);
  const onClickableMouseUp = useCallback(() => {
    setIsActive(true);
  }, []);
  const onClickableMouseOut = useCallback(() => {
    setIsActive(false);
    setIsActiveClickable(false);
  }, []);

  useEffect(() => {
    const clickableElements = document.querySelectorAll(clickables.join(","));

    clickableElements.forEach((el) => {
      el.style.cursor = "none";
      el.addEventListener("mouseover", onClickableMouseOver);
      el.addEventListener("click", onClickableClick);
      el.addEventListener("mousedown", onClickableMouseDown);
      el.addEventListener("mouseup", onClickableMouseUp);
      el.addEventListener("mouseout", onClickableMouseOut);
    });

    return () => {
      clickableElements.forEach((el) => {
        el.style.cursor = "";
        el.removeEventListener("mouseover", onClickableMouseOver);
        el.removeEventListener("click", onClickableClick);
        el.removeEventListener("mousedown", onClickableMouseDown);
        el.removeEventListener("mouseup", onClickableMouseUp);
        el.removeEventListener("mouseout", onClickableMouseOut);
      });
    };
  }, [
    clickables,
    onClickableMouseOver,
    onClickableClick,
    onClickableMouseDown,
    onClickableMouseUp,
    onClickableMouseOut,
  ]);

  useEffect(() => {
    const previousCursor = document.body.style.cursor;
    document.body.style.cursor = "none";

    return () => {
      document.body.style.cursor = previousCursor;
    };
  }, []);

  const styles = useMemo(
    () => ({
      cursorInner: {
        zIndex: 999,
        display: "block",
        position: "fixed",
        borderRadius: "50%",
        width: innerSize,
        height: innerSize,
        pointerEvents: "none",
        backgroundColor: `rgba(${color}, 1)`,
        transition: "opacity 0.15s ease-in-out, transform 0.25s ease-in-out",
        ...(innerStyle || {}),
      },
      cursorOuter: {
        zIndex: 999,
        display: "block",
        position: "fixed",
        borderRadius: "50%",
        pointerEvents: "none",
        width: outerSize,
        height: outerSize,
        backgroundColor: `rgba(${color}, ${outerAlpha})`,
        transition: "opacity 0.15s ease-in-out, transform 0.15s ease-in-out",
        willChange: "transform",
        ...(outerStyle || {}),
      },
    }),
    [color, outerAlpha, innerSize, innerStyle, outerSize, outerStyle]
  );

  return (
    <React.Fragment>
      <div ref={cursorOuterRef} style={styles.cursorOuter} />
      <div ref={cursorInnerRef} style={styles.cursorInner} />
    </React.Fragment>
  );
}

function AnimatedCursor({
  outerStyle,
  innerStyle,
  color,
  outerAlpha,
  innerSize,
  innerScale,
  outerSize,
  outerScale,
  trailingSpeed,
  clickables,
}) {
  if (typeof navigator !== "undefined" && DEVICE?.any()) {
    return <React.Fragment />;
  }

  return (
    <CursorCore
      outerStyle={outerStyle}
      innerStyle={innerStyle}
      color={color}
      outerAlpha={outerAlpha}
      innerSize={innerSize}
      innerScale={innerScale}
      outerSize={outerSize}
      outerScale={outerScale}
      trailingSpeed={trailingSpeed}
      clickables={clickables}
    />
  );
}

const styleShape = PropTypes.objectOf(
  PropTypes.oneOfType([PropTypes.string, PropTypes.number])
);

CursorCore.propTypes = {
  outerStyle: styleShape,
  innerStyle: styleShape,
  color: PropTypes.string,
  outerAlpha: PropTypes.number,
  innerSize: PropTypes.number,
  outerSize: PropTypes.number,
  outerScale: PropTypes.number,
  innerScale: PropTypes.number,
  trailingSpeed: PropTypes.number,
  clickables: PropTypes.arrayOf(PropTypes.string),
};

AnimatedCursor.propTypes = CursorCore.propTypes;

export default AnimatedCursor;
